import { ContentContext, CountryContext, DatelineContext, LocaleContext, RegionContext } from 'Components/hooks/context';
import React, { useContext, useEffect } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import Head from 'next/head';
import mustache from 'mustache';
import { useRouter } from 'next/router';

const atlas = new AtlasMetadataClient();

const getShareImgPath = (locale, country, region) => {
  const ROOT_PATH = 'https://graphics.thomsonreuters.com/cdn/2020/covid-global-tracker/share-cards';
  if (country) {
    return `${ROOT_PATH}/countries/${locale}/${country.slug}.png`;
  }
  if (region) {
    return `${ROOT_PATH}/regions/${locale}/${region.slug}.png`;
  }
  return `${ROOT_PATH}/global/${locale}/share.png`;
};

const ensureRestful = (url) => {
  if ((/\/$/g).test(url)) return url;
  return url + '/';
};

const HeadComponent = (props) => {
  const router = useRouter();
  const { pathname } = router;

  const locale = useContext(LocaleContext);
  const dateline = useContext(DatelineContext);
  const regionSlug = useContext(RegionContext);
  const region = atlas.getRegion(regionSlug);
  const countrySlug = useContext(CountryContext);
  const country = atlas.getCountry(countrySlug);
  const content = useContext(ContentContext);

  const renderContext = {
    country: country ? country.translations[locale] : null,
    region: region ? region.translations[locale] : null,
  };

  const { metadata } = content.template;

  const seoTitle = mustache.render(metadata.seoTitle, renderContext);
  const shareTitle = mustache.render(metadata.shareTitle, renderContext);
  const seoDescription = mustache.render(metadata.seoDescription, renderContext);
  const shareDescription = mustache.render(metadata.shareDescription, renderContext);

  const pagePath = ensureRestful('https://graphics.reuters.com/world-coronavirus-tracker-and-maps' + router.asPath);

  const shareImgPath = props.overrideShareImgPath || getShareImgPath(locale, country, region);
  const shareImgDims = { width: '1200px', height: '628px' };

  const jsonLdOrg = {
    '@context': 'http://schema.org',
    '@type': 'NewsMediaOrganization',
    '@id': 'https://www.reuters.com/#publisher',
    name: 'Reuters',
    logo: {
      '@type': 'ImageObject',
      url: 'https://s3.reutersmedia.net/resources_v2/images/reuters_social_logo.png',
      width: '200',
      height: '200',
    },
    url: 'https://www.reuters.com/',
  };

  const jsonLd = {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    headline: seoTitle,
    url: pagePath,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pagePath,
    },
    thumbnailUrl: shareImgPath,
    image: [{
      '@context': 'http://schema.org',
      '@type': 'ImageObject',
      url: shareImgPath,
      width: shareImgDims.width,
      height: shareImgDims.height,
    }],
    publisher: { '@id': 'https://www.reuters.com/#publisher' },
    copyrightHolder: { '@id': 'https://www.reuters.com/#publisher' },
    sourceOrganization: { '@id': 'https://www.reuters.com/#publisher' },
    copyrightYear: '2020',
    dateCreated: props.overrideDatePublished || '2020-09-14T06:00:00.000Z',
    datePublished: props.overrideDatePublished || '2020-09-14T06:00:00.000Z',
    dateModified: dateline,
    author: [{
      '@type': 'Person',
      name: 'Gurman Bhatia',
      url: 'https://twitter.com/GurmanBhatia',
    }, {
      '@type': 'Person',
      name: 'Prasanta Kumar Dutta',
      url: 'https://twitter.com/Da_Pacific',
    }, {
      '@type': 'Person',
      name: 'Jon McClure',
      url: 'https://twitter.com/JonRMcClure',
    }],
    articleSection: 'Graphics',
    isAccessibleForFree: true,
    creator: ['Reuters Graphics'],
    keywords: ['coronavirus', 'covid-19', 'covid-19 tracker', 'coronavirus tracker', 'maps', 'charts', 'statistics', 'Reuters graphics', 'Reuters', 'graphics', 'Interactives'],
  };

  // Analytics pageviews
  useEffect(() => {
    // Slight delay to allow document title to update
    setTimeout(() => {
      if (window.location.host !== 'graphics.reuters.com') return;
      window.ga('send', 'pageview', {
        page: window.location.href,
        title: document.title,
      });
    }, 500);
  }, [pathname]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <Head>
      <html lang={locale} />
      <meta charSet='utf8' />
      <meta name='viewport' content='width=device-width, initial-scale=1.0' />
      <meta httpEquiv='cleartype' content='on' />
      <meta httpEquiv='X-UA-Compatible' content='IE=edge,chrome=1' />
      <title>{seoTitle}</title>
      <meta name='description' content={seoDescription} />

      <link rel='icon' type='image/png' href='https://fingfx.thomsonreuters.com/gfx/rngs/TEST-TEST/010062QJ4J3/favicon.ico' />
      <link rel='shortcut icon' href='https://fingfx.thomsonreuters.com/gfx/rngs/TEST-TEST/010062QJ4J3/favicon.ico' />
      <link rel='icon' type='image/png' href='https://s3.reutersmedia.net/resources_v2/images/favicon/favicon-16x16.png' sizes='16x16' />
      <link rel='icon' type='image/png' href='https://s1.reutersmedia.net/resources_v2/images/favicon/favicon-32x32.png' sizes='32x32' />
      <link rel='icon' type='image/png' href='https://s3.reutersmedia.net/resources_v2/images/favicon/favicon-96x96.png' sizes='96x96' />

      <meta property='fb:app_id' content='319194411438328' />
      <meta property='fb:admins' content='616167736' />
      <meta property='fb:admins' content='625796953' />
      <meta property='fb:admins' content='571759798' />

      <meta property='og:url' content={pagePath} />
      <meta property='og:type' content='article' />
      <meta property='og:site_name' content='Reuters' />
      <meta property='og:title' content={shareTitle} itemProp='name' />
      <meta property='og:description' content={shareDescription} itemProp='description' />
      <meta property='og:image' content={shareImgPath} itemProp='image' />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:site' content='@ReutersGraphics' />
      <meta name='twitter:creator' content='@ReutersGraphics' />
      <meta name='twitter:domain' content='https://graphics.reuters.com/' />
      <meta name='twitter:title' content={shareTitle} />
      <meta name='twitter:description' content={shareDescription} />
      <meta name='twitter:image:src' content={shareImgPath} />

      <link rel='canonical' href={pagePath} />

      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrg) }}
      />
    </Head>
  );
};

export default HeadComponent;
